/**
 * Samples: AJAX add to cart without opening cart drawer; header dropdown of sample cart lines.
 */

const SAMPLE_SECTION_SEL = '[data-samples-root]';

let sampleCartLimitPubSubWired = false;

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function featuredImageSrc(p) {
  const fi = p.featured_image;
  if (!fi) return '';
  if (typeof fi === 'string') return fi;
  return fi.src || '';
}

function lineItemMatchesSample(line, propertyKey, propertyValue) {
  const props = line.properties || {};
  const v = props[propertyKey];
  return propertyValue !== '' ? String(v) === String(propertyValue) : props[propertyKey] != null;
}

/** Total quantity of cart lines tagged as samples (matching key + value). */
function sampleLinesTotalQuantity(cart, propertyKey, propertyValue) {
  if (!cart?.items?.length) return 0;
  return cart.items.reduce((sum, line) => {
    return sum + (lineItemMatchesSample(line, propertyKey, propertyValue) ? Number(line.quantity) || 0 : 0);
  }, 0);
}

/** Whether the cart already has any sample-tagged line for this Shopify product ID. */
function cartHasSampleForProduct(cart, productId, propertyKey, propertyValue) {
  if (!cart?.items?.length || productId == null || productId === '') return false;
  const want = Number(productId);
  if (!Number.isFinite(want)) return false;
  return cart.items.some((line) => {
    if (!lineItemMatchesSample(line, propertyKey, propertyValue)) return false;
    const pid = Number(line.product_id);
    return Number.isFinite(pid) && pid === want;
  });
}

function parseSampleCartLimit(rootElement) {
  if (!rootElement) return null;
  const raw = rootElement.dataset?.sampleCartLimit;
  const n = parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n) || n < 1) return null;
  return n;
}

/** First Samples section whose sample property matches the header menu (`data-property-key` / `data-property-value`). */
function findMatchingSamplesRoot(menuEl) {
  if (!(menuEl instanceof HTMLElement)) return null;
  const pk = menuEl.dataset.propertyKey ?? '_sample';
  const pv = menuEl.dataset.propertyValue ?? '1';
  /** @type {NodeListOf<HTMLElement>} */
  const roots = document.querySelectorAll(SAMPLE_SECTION_SEL);
  for (const root of roots) {
    const rk = root.dataset.samplePropertyKey ?? '_sample';
    const rv = root.dataset.samplePropertyValue ?? '1';
    if (rk === pk && String(rv) === String(pv)) return root;
  }
  return null;
}

/** Prefer limit from a matching Samples section (where merchants usually set it); else header `data-sample-cart-limit`. */
function getEffectiveSampleCartLimit(menuEl) {
  const root = findMatchingSamplesRoot(menuEl);
  if (root instanceof HTMLElement) {
    const fromSection = parseSampleCartLimit(root);
    if (fromSection != null) return fromSection;
  }
  return parseSampleCartLimit(menuEl);
}

/** Prefer message from matching Samples section; else header; falls back only when trimming leaves empty at render time. */
function getEffectiveSampleLimitMessage(menuEl) {
  const root = findMatchingSamplesRoot(menuEl);
  const fromSection = root ? String(root.dataset.sampleLimitMessage || '').trim() : '';
  const fromHeader = String(menuEl instanceof HTMLElement ? menuEl.dataset.sampleLimitMessage || '' : '').trim();
  const fallback = 'You have reached the maximum number of samples.';
  return fromSection || fromHeader || fallback;
}

/**
 * Enables or disables sample order CTAs under a Samples section root for the given cart snapshot.
 */
function applySampleLimitToRoot(rootElement, cart) {
  if (!cart || cart.errors) return;

  const pk = rootElement.dataset.samplePropertyKey || '_sample';
  const pv = rootElement.dataset.samplePropertyValue || '1';
  const limitMsg = (rootElement.dataset.sampleLimitMessage || '').trim();
  const dupMsg = (rootElement.dataset.sampleDuplicateMessage || '').trim();
  const orderLabel = (rootElement.dataset.orderSampleLabel || '').trim() || 'Order sample';
  const addedLabel = (rootElement.dataset.sampleAddedLabel || '').trim() || 'Added!';
  const maxQty = parseSampleCartLimit(rootElement);
  const qty = sampleLinesTotalQuantity(cart, pk, pv);
  const atLimit = maxQty != null && qty >= maxQty;

  rootElement.querySelectorAll('form[data-sample-order-form]').forEach((form) => {
    const btn = form.querySelector('button[type="submit"]');
    if (!btn) return;

    const labelEl = btn.querySelector('.btn-text');
    const productTitle = (form.dataset.sampleProductTitle || '').trim();
    function ariaOrder() {
      return [orderLabel, productTitle].filter(Boolean).join(' ');
    }
    function ariaAdded() {
      return [addedLabel, productTitle].filter(Boolean).join(' ');
    }

    const productId = form.dataset.sampleProductId;
    const duplicate = !!(productId && cartHasSampleForProduct(cart, productId, pk, pv));

    const showAddedLabel = duplicate;
    if (labelEl) labelEl.textContent = showAddedLabel ? addedLabel : orderLabel;
    btn.setAttribute('aria-label', showAddedLabel ? ariaAdded() : ariaOrder());

    if (atLimit) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      if (limitMsg) btn.setAttribute('title', limitMsg);
      else btn.removeAttribute('title');
    } else if (duplicate) {
      btn.disabled = true;
      btn.setAttribute('aria-disabled', 'true');
      if (dupMsg) btn.setAttribute('title', dupMsg);
      else btn.removeAttribute('title');
    } else {
      btn.disabled = false;
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('title');
    }
  });
}

async function refreshAllSampleSectionsFromCart(cartMaybe) {
  try {
    const cart = cartMaybe && !cartMaybe.errors ? cartMaybe : await fetchCartJson();
    if (!cart || cart.errors) return;
    document.querySelectorAll(SAMPLE_SECTION_SEL).forEach((root) => applySampleLimitToRoot(root, cart));
  }
  catch {
    /* ignore */
  }
}

function ensureSampleCartLimitPubSub() {
  if (sampleCartLimitPubSubWired) return;
  sampleCartLimitPubSubWired = true;
  if (typeof theme === 'undefined' || !theme.pubsub || !theme.pubsub.subscribe) return;
  theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (payload) => {
    refreshAllSampleSectionsFromCart(payload?.cart).catch(() => {});
  });
}

/** Shopify cart.js lines use `image` (string URL); some payloads use `featured_image`. */
function cartLineImage(line) {
  if (line.image) {
    return { url: line.image, alt: line.title };
  }
  const fi = line.featured_image;
  if (typeof fi === 'string' && fi) {
    return { url: fi, alt: line.title };
  }
  if (fi && typeof fi.url === 'string') {
    return { url: fi.url, alt: fi.alt || line.title };
  }
  return null;
}

async function fetchCartJson() {
  const res = await fetch(`${theme.routes.cart_url}.js`, { ...theme.utils.fetchConfig('json', 'GET') });
  return res.json();
}

async function cartChangeQuantity(lineKey, quantity) {
  const body = JSON.stringify({ id: lineKey, quantity });
  const res = await fetch(theme.routes.cart_change_url, {
    ...theme.utils.fetchConfig('javascript'),
    headers: {
      ...theme.utils.fetchConfig('javascript').headers,
      'Content-Type': 'application/json',
    },
    body,
  });
  return res.json();
}

/**
 * @param {object} line
 * @param {string} removeLabel
 */
function renderSampleCartLine(line, removeLabel) {
  const row = document.createElement('div');
  row.className = 'samples-cart__bubble';

  const ci = cartLineImage(line);
  const imgWrap = document.createElement('div');
  imgWrap.className = 'samples-cart__bubble-img';
  if (ci?.url) {
    const img = document.createElement('img');
    img.src = ci.url;
    img.alt = ci.alt || '';
    img.loading = 'lazy';
    img.decoding = 'async';
    img.width = 56;
    img.height = 56;
    imgWrap.appendChild(img);
  }

  const body = document.createElement('div');
  body.className = 'samples-cart__bubble-body';
  const title = document.createElement('a');
  title.className = 'samples-cart__bubble-title reversed-link';
  title.href = line.url;
  title.textContent = line.product_title || line.title;

  const meta = document.createElement('p');
  meta.className = 'samples-cart__bubble-meta';
  meta.textContent = line.variant_title && line.variant_title !== 'Default Title' ? line.variant_title : '';

  const actions = document.createElement('div');
  actions.className = 'samples-cart__bubble-actions';
  let remove;
  try {
    remove = document.createElement('button', { is: 'hover-button' });
  } catch {
    remove = document.createElement('button');
  }
  remove.type = 'button';
  remove.className = 'button button--secondary button--small samples-cart__remove';
  if (!remove.getAttribute('is')) remove.setAttribute('is', 'hover-button');
  const fb = document.createElement('span');
  fb.className = 'btn-fill';
  fb.setAttribute('data-fill', '');
  const btnTextRemove = document.createElement('span');
  btnTextRemove.className = 'btn-text';
  btnTextRemove.textContent = removeLabel || 'Remove';
  remove.append(fb, btnTextRemove);
  actions.appendChild(remove);
  if (typeof customElements.upgrade === 'function') customElements.upgrade(remove);
  remove.addEventListener('click', async () => {
    remove.setAttribute('aria-busy', 'true');
    await cartChangeQuantity(line.key, 0);
    const cartJson = await fetchCartJson();
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'samples-header-menu', cart: cartJson });
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    remove.removeAttribute('aria-busy');
  });

  body.append(title, meta, actions);
  row.append(imgWrap, body);
  return row;
}

/**
 * AJAX add to cart without opening the cart drawer.
 */
async function onSampleOrderFormSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-sample-order-form')) return;

  event.preventDefault();

  const submitButton = form.querySelector('[type="submit"]');
  if (!submitButton || submitButton.hasAttribute('aria-disabled')) return;

  const root = form.closest(SAMPLE_SECTION_SEL);
  if (root) {
    try {
      const cartBefore = await fetchCartJson();
      const pk = root.dataset.samplePropertyKey || '_sample';
      const pv = root.dataset.samplePropertyValue || '1';
      const maxQty = parseSampleCartLimit(root);
      if (maxQty != null && sampleLinesTotalQuantity(cartBefore, pk, pv) >= maxQty) {
        await refreshAllSampleSectionsFromCart(cartBefore);
        return;
      }
      const productId = form.dataset.sampleProductId;
      if (productId && cartHasSampleForProduct(cartBefore, productId, pk, pv)) {
        await refreshAllSampleSectionsFromCart(cartBefore);
        return;
      }
    } catch {
      /* continue and let add-to-cart handle errors */
    }
  }

  const config = theme.utils.fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData(form);
  formData.append('sections_url', window.location.pathname);
  config.body = formData;

  submitButton.setAttribute('aria-disabled', 'true');
  submitButton.setAttribute('aria-busy', 'true');

  fetch(theme.routes.cart_add_url, config)
    .then((response) => response.json())
    .then(async (parsedState) => {
      if (parsedState.status) {
        const cartJson = await fetchCartJson();
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'sample-order-form',
          productVariantId: formData.get('id'),
          cart: cartJson,
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        return;
      }
      const cartJson = await fetchCartJson();
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source: 'sample-order-form',
        productVariantId: formData.get('id'),
        cart: cartJson,
      });
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: { product: parsedState },
        })
      );
      const sectionId = form.closest(SAMPLE_SECTION_SEL)?.dataset?.sectionId ?? null;
      const inSamplePopup = !!form.closest('#SamplePagePopup, #SamplePagePopupBody');
      document.dispatchEvent(
        new CustomEvent('samples:added', {
          bubbles: true,
          detail: {
            sectionId,
            source: 'sample-order-form',
            skipHeaderDropdown: inSamplePopup,
          },
        })
      );
    })
    .catch(() => {})
    .finally(() => {
      submitButton.removeAttribute('aria-busy');
      submitButton.removeAttribute('aria-disabled');
    });
}

if (!window.__samplesOrderFormDelegate) {
  window.__samplesOrderFormDelegate = true;
  document.addEventListener('submit', onSampleOrderFormSubmit);
}

function throttleSamplesDropdown(fn, wait) {
  let t = 0;
  let pending = false;
  return function throttled() {
    const now = Date.now();
    const run = () => {
      pending = false;
      fn();
    };
    if (now - t >= wait) {
      t = now;
      run();
    } else if (!pending) {
      pending = true;
      setTimeout(() => {
        t = Date.now();
        run();
      }, wait - (now - t));
    }
  };
}

/** Port panel to {@link document.body} while open — beats late-loaded chat z-order and escapes `body { overflow-x: hidden }` clipping. */
const DetailsDropdownCtor =
  typeof customElements !== 'undefined' ? customElements.get('details-dropdown') : null;

if (DetailsDropdownCtor && !customElements.get('samples-details-dropdown')) {
  class SamplesDetailsDropdown extends DetailsDropdownCtor {
    get samplesPanel() {
      const ref = this.__samplesDropdownPanelRef;
      if (ref instanceof HTMLElement && ref.isConnected) {
        return ref;
      }
      const node = this.querySelector('.samples-header-menu__dropdown');
      if (node instanceof HTMLElement) this.__samplesDropdownPanelRef = node;
      return this.__samplesDropdownPanelRef ?? null;
    }

    /** After moving the panel to `body`, details cannot reach it via `querySelector` — cache before reparenting. */
    get contentElement() {
      return this.samplesPanel ?? super.contentElement;
    }

    shouldReverse() {
      this.samplesPanel?.classList.remove('should-reverse');
    }

    detectClickOutside(event) {
      const panel = this.samplesPanel;
      if (panel?.contains(event.target)) return;
      if (this.contains(event.target)) return;
      const nearestDetails = event.target.closest('details');
      if (nearestDetails instanceof DetailsDropdownCtor) return;
      this.open = false;
    }

    detectFocusOut(event) {
      const rt = event.relatedTarget;
      if (!rt) return;
      const panel = this.samplesPanel;
      if (this.contains(rt) || panel?.contains(rt)) return;
      this.open = false;
    }

    repositionPanelToBody() {
      const panel = this.samplesPanel;
      if (!panel || panel.parentElement === document.body) return;
      document.body.appendChild(panel);
    }

    restorePanelInsideDetails() {
      const panel = this.samplesPanel;
      if (!panel || panel.parentElement === this) return;
      this.appendChild(panel);
    }

    async transitionIn() {
      this.repositionPanelToBody();
      return super.transitionIn();
    }

    async transitionOut() {
      try {
        return await super.transitionOut();
      } finally {
        this.restorePanelInsideDetails();
      }
    }
  }
  customElements.define('samples-details-dropdown', SamplesDetailsDropdown, { extends: 'details' });
}

function closeAllSamplesHeaderMenus() {
  document.querySelectorAll('samples-header-menu details.samples-header-menu__details').forEach((details) => {
    if (details.open) details.open = false;
  });
}

document.addEventListener('header:close-samples-menu', closeAllSamplesHeaderMenus);

class SamplesHeaderMenu extends HTMLElement {
  connectedCallback() {
    if (this.__samplesHeaderMenuWired) return;
    this.__samplesHeaderMenuWired = true;

    this.propertyKey = this.dataset.propertyKey || '_sample';
    this.propertyValue = this.dataset.propertyValue || '1';

    this.list = this.querySelector('[data-samples-header-list]');
    this.badges = this.querySelectorAll('[data-samples-header-badge]');
    this.detailsEl = this.querySelector('details.samples-header-menu__details');
    this.dropdownEl = this.querySelector('.samples-header-menu__dropdown');
    this.limitBanner = this.querySelector('[data-samples-header-limit-banner]');

    this.cartUnsub = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, () => {
      this.refreshFromCart();
    });

    this.onSamplesAdded = (event) => {
      const skipDropdown = event?.detail?.skipHeaderDropdown === true;
      this.refreshFromCart()
        .catch(() => {})
        .finally(() => {
          if (!skipDropdown) {
            queueMicrotask(() => this._tryOpenDropdownAfterSampleAdd());
          }
        });
    };
    document.addEventListener('samples:added', this.onSamplesAdded);

    this._samplesDropdownAnchorBound =
      typeof theme !== 'undefined' && typeof theme.utils?.throttle === 'function'
        ? theme.utils.throttle(() => this._syncSamplesDropdownAnchor(), 80)
        : throttleSamplesDropdown(() => this._syncSamplesDropdownAnchor(), 100);
    this._samplesDropdownToggle = () => this._onSamplesDropdownToggle();
    if (this.detailsEl && this.dropdownEl) {
      this.detailsEl.addEventListener('toggle', this._samplesDropdownToggle);
      window.addEventListener('resize', this._samplesDropdownAnchorBound);
      window.addEventListener('scroll', this._samplesDropdownAnchorBound, true);
      document.addEventListener('header:scrolled', this._samplesDropdownAnchorBound);
      /** @type {ResizeObserver | null} */
      this._samplesDropdownHeaderResizeObs = null;
      const headerChrome =
        document.querySelector('.header-section') || document.querySelector('.header-section .header');
      if (headerChrome && typeof ResizeObserver !== 'undefined') {
        this._samplesDropdownHeaderResizeObs = new ResizeObserver(() => {
          if (this.detailsEl?.open) {
            requestAnimationFrame(() => this._syncSamplesDropdownAnchor());
          }
        });
        this._samplesDropdownHeaderResizeObs.observe(headerChrome);
      }
    }

    this.refreshFromCart();
  }

  disconnectedCallback() {
    queueMicrotask(() => {
      if (document.documentElement.contains(this)) {
        return;
      }
      if (!this.__samplesHeaderMenuWired) return;
      document.removeEventListener('samples:added', this.onSamplesAdded);
      if (this.cartUnsub) this.cartUnsub();
      if (this.detailsEl && this._samplesDropdownToggle) {
        this.detailsEl.removeEventListener('toggle', this._samplesDropdownToggle);
      }
      if (this._samplesDropdownAnchorBound) {
        window.removeEventListener('resize', this._samplesDropdownAnchorBound);
        window.removeEventListener('scroll', this._samplesDropdownAnchorBound, true);
        document.removeEventListener('header:scrolled', this._samplesDropdownAnchorBound);
      }
      if (this._samplesDropdownHeaderResizeObs) {
        try {
          this._samplesDropdownHeaderResizeObs.disconnect();
        } catch {
          /* ignore */
        }
        this._samplesDropdownHeaderResizeObs = null;
      }
      document.documentElement.classList.remove('samples-header-dropdown-open');
      document.body.classList.remove('samples-header-dropdown-open');
      if (this.detailsEl && this.dropdownEl?.parentElement === document.body) {
        this.detailsEl.appendChild(this.dropdownEl);
      }
      this.dropdownEl?.style.removeProperty('--samples-dropdown-top');
      this.__samplesHeaderMenuWired = false;
    });
  }

  _onSamplesDropdownToggle() {
    const open = !!this.detailsEl?.open;
    if (open) {
      document.dispatchEvent(new CustomEvent('header:close-crisp-chat'));
    }
    document.documentElement.classList.toggle('samples-header-dropdown-open', open);
    document.body.classList.toggle('samples-header-dropdown-open', open);
    queueMicrotask(() => this._syncSamplesDropdownAnchor());
  }

  /** Open header samples menu after a successful “Order sample” add (desktop only; matches `lg` visibility). */
  _tryOpenDropdownAfterSampleAdd() {
    if (!this.detailsEl || !document.documentElement.contains(this)) return;
    const triggerMode = this.detailsEl.getAttribute('trigger') || 'click';
    const touch =
      typeof theme !== 'undefined' && theme.config && theme.config.isTouch === true;
    if (triggerMode === 'hover' && !touch) return;

    try {
      if (typeof window.matchMedia !== 'function' || !window.matchMedia('(min-width: 1024px)').matches) return;
      this.detailsEl.open = true;
    } catch {
      return;
    }
    queueMicrotask(() => {
      this._onSamplesDropdownToggle();
      requestAnimationFrame(() => {
        this._syncSamplesDropdownAnchor();
        requestAnimationFrame(() => this._syncSamplesDropdownAnchor());
      });
    });
  }

  /**
   * Place fixed panel flush under rendered header chrome (fixes two-row layouts & timing vs --header-height).
   */
  _syncSamplesDropdownAnchor() {
    if (!this.detailsEl?.open || !this.dropdownEl) {
      this.dropdownEl?.style.removeProperty('--samples-dropdown-top');
      return;
    }
    const header = document.querySelector('.header-section .header');
    const anchor = document.querySelector('.header-section') || header;
    if (!anchor || typeof anchor.getBoundingClientRect !== 'function') return;
    const top = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
    this.dropdownEl.style.setProperty('--samples-dropdown-top', `${top}px`);
    requestAnimationFrame(() => {
      if (!this.detailsEl?.open || !this.dropdownEl) return;
      const second = Math.max(0, Math.round(anchor.getBoundingClientRect().bottom));
      this.dropdownEl.style.setProperty('--samples-dropdown-top', `${second}px`);
    });
  }

  async refreshFromCart() {
    if (!this.list) return;
    try {
      const cart = await fetchCartJson();
      const items = (cart.items || []).filter((line) =>
        lineItemMatchesSample(line, this.propertyKey, this.propertyValue)
      );
      const count = items.reduce((s, l) => s + l.quantity, 0);
      const removeLabel = this.dataset.removeLabel || 'Remove';
      const emptyText = this.dataset.emptyText || '';

      const maxQty = getEffectiveSampleCartLimit(this);
      const limitMsg = getEffectiveSampleLimitMessage(this);
      if (this.limitBanner instanceof HTMLElement) {
        const atLimit = maxQty != null && count >= maxQty;
        if (atLimit) {
          this.limitBanner.textContent = limitMsg;
          this.limitBanner.hidden = false;
        } else {
          this.limitBanner.hidden = true;
          this.limitBanner.textContent = '';
        }
      }

      this.badges.forEach((badge) => {
        badge.textContent = String(count);
        badge.toggleAttribute('hidden', count < 1);
      });

      this.list.innerHTML = '';
      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'samples-cart__empty';
        empty.textContent = emptyText;
        this.list.appendChild(empty);
      } else {
        items.forEach((line) => {
          this.list.appendChild(renderSampleCartLine(line, removeLabel));
        });
      }
    } catch {
      /* ignore */
    }
  }
}

if (!customElements.get('samples-header-menu')) {
  customElements.define('samples-header-menu', SamplesHeaderMenu);
}

/** @param {HTMLElement} root */
function initTabClicks(root) {
  const tabs = root.querySelectorAll('.samples-tablist button[role="tab"]');
  const panels = root.querySelectorAll('[data-samples-collection-panel]');

  function activate(i) {
    if (root.dataset.layout === 'list') return;
    tabs.forEach((btn, j) => {
      const on = j === i;
      btn.classList.toggle('button--primary', on);
      btn.classList.toggle('button--secondary', !on);
      btn.disabled = on;
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    panels.forEach((p, j) => {
      p.toggleAttribute('hidden', j !== i);
    });
    root.querySelectorAll('.samples-tab-indicators').forEach((wrap, j) => {
      wrap.toggleAttribute('hidden', j !== i);
    });
  }

  tabs.forEach((btn, i) => {
    btn.addEventListener('click', () => activate(i));
  });

  root.samplesActivateTab = activate;
  if (root.dataset.layout !== 'list') activate(0);
}

/** @param {HTMLElement} root */
function initLayoutSwitcher(root) {
  const showSwitcher = root.dataset.showLayoutSwitcher === 'true';
  const defaultMode = root.dataset.layoutDefault || 'tabs';
  const tabBtn = root.querySelector('[data-samples-layout-toggle="tabs"]');
  const listBtn = root.querySelector('[data-samples-layout-toggle="list"]');
  const tablist = root.querySelector('.samples-tablist');

  function applyLayout(next) {
    root.dataset.layout = next;
    if (tabBtn && listBtn) {
      tabBtn.classList.toggle('button--primary', next === 'tabs');
      tabBtn.classList.toggle('button--secondary', next !== 'tabs');
      listBtn.classList.toggle('button--primary', next === 'list');
      listBtn.classList.toggle('button--secondary', next !== 'list');
      tabBtn.setAttribute('aria-pressed', next === 'tabs' ? 'true' : 'false');
      listBtn.setAttribute('aria-pressed', next === 'list' ? 'true' : 'false');
    }
    if (tablist) tablist.toggleAttribute('hidden', next === 'list');
    if (next === 'list') {
      root.querySelectorAll('[data-samples-collection-panel]').forEach((p) => {
        p.removeAttribute('hidden');
      });
      root.querySelectorAll('.samples-tab-indicators').forEach((el) => el.removeAttribute('hidden'));
    } else if (typeof root.samplesActivateTab === 'function') {
      root.samplesActivateTab(0);
    }
  }

  if (showSwitcher && tabBtn && listBtn) {
    tabBtn.addEventListener('click', () => applyLayout('tabs'));
    listBtn.addEventListener('click', () => applyLayout('list'));
  }
  applyLayout(root.dataset.layout || defaultMode);
}

/** @param {HTMLElement} root */
function initLoadMore(root) {
  const btn = root.querySelector('[data-samples-load-more]');
  if (!btn) return;

  const perPage = parseInt(root.dataset.perPage || '8', 10);
  const samplePropertyKey = root.dataset.samplePropertyKey || '_sample';
  const samplePropertyValue = root.dataset.samplePropertyValue || '1';
  const orderLabel = root.dataset.orderSampleLabel || 'Order sample';
  const imageRatio = root.dataset.imageRatio || 'square';

  /** @type {Record<string, { products: any[]; done: boolean; page: number }>} */
  const cache = {};

  async function fetchPage(handle, page) {
    const url = `${theme.routes.root_url}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('products.json');
    return res.json();
  }

  async function ensureCache(handle) {
    if (!cache[handle]) {
      cache[handle] = { products: [], page: 0, done: false };
    }
    const bucket = cache[handle];
    if (bucket.done) return bucket;

    while (!bucket.done) {
      bucket.page += 1;
      const json = await fetchPage(handle, bucket.page);
      const chunk = json.products || [];
      for (const p of chunk) {
        bucket.products.push(p);
      }
      if (chunk.length < 250) bucket.done = true;
    }
    return bucket;
  }

  btn.addEventListener('click', async () => {
    btn.setAttribute('aria-busy', 'true');
    btn.setAttribute('disabled', '');

    try {
      const targets = root.querySelectorAll('[data-samples-collection-body][data-has-more="true"]');
      for (const body of targets) {
        const handle = body.dataset.collectionHandle;
        const offset = parseInt(body.dataset.offset || '0', 10);
        if (!handle) continue;

        const bucket = await ensureCache(handle);
        const slice = bucket.products.slice(offset, offset + perPage);
        body.dataset.offset = String(offset + slice.length);
        if (offset + slice.length >= bucket.products.length) {
          body.dataset.hasMore = 'false';
        }

        const grid = body.querySelector('.samples-product-grid');
        if (!grid) continue;

        for (const p of slice) {
          const variant = (p.variants || []).find((v) => v.available) || p.variants?.[0];
          if (!variant) continue;
          const card = document.createElement('div');
          card.className = 'card product-card flex flex-col leading-none relative';
          const src = featuredImageSrc(p);
          const img = src
            ? `<a class="block media media--${escapeHtml(imageRatio)} overflow-hidden" href="${escapeHtml(p.url)}"><img src="${escapeHtml(
                src
              )}" alt="${escapeHtml(p.title)}" loading="lazy" width="360" height="360" class="w-full h-full object-cover"></a>`
            : `<div class="media media--${escapeHtml(imageRatio)}"><span class="block w-full aspect-square bg-black/5"></span></div>`;

          const productTitleEscaped = escapeHtml(p.title || '');
          const formOrLink = `<form data-sample-order-form data-sample-product-id="${escapeHtml(String(p.id))}" data-sample-product-title="${productTitleEscaped}" action="${theme.routes.cart_add_url}" method="post" enctype="multipart/form-data" class="block w-full mt-2">
                  <input type="hidden" name="id" value="${variant.id}">
                  <input type="hidden" name="quantity" value="1">
                  <input type="hidden" name="properties[${escapeHtml(samplePropertyKey)}]" value="${escapeHtml(samplePropertyValue)}">
                  <button type="submit" class="button button--primary w-full" is="hover-button" aria-label="${escapeHtml(orderLabel)} ${productTitleEscaped}"><span class="btn-fill" data-fill></span><span class="btn-text">${escapeHtml(
                    orderLabel
                  )}</span></button>
                </form>`;

          card.innerHTML = `
            <div class="flex flex-col flex-1">
              ${img}
              <div class="product-card__content grow flex flex-col gap-2 pt-3">
                <a class="product-card__title reversed-link text-base font-medium leading-tight" href="${escapeHtml(p.url)}">${escapeHtml(p.title)}</a>
                ${formOrLink}
              </div>
            </div>`;
          grid.appendChild(card);
          if (typeof customElements.upgrade === 'function') customElements.upgrade(card);
        }
      }

      const anyMore = [...root.querySelectorAll('[data-samples-collection-body]')].some((b) => b.dataset.hasMore === 'true');
      if (!anyMore) {
        btn.setAttribute('hidden', '');
      }
      await refreshAllSampleSectionsFromCart();
    } catch (e) {
      console.error(e);
    } finally {
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('disabled');
    }
  });
}

function bindSamplesSections() {
  ensureSampleCartLimitPubSub();
  document.querySelectorAll(SAMPLE_SECTION_SEL).forEach((root) => {
    if (root.dataset.samplesSectionJsBound === '1') return;
    root.dataset.samplesSectionJsBound = '1';
    initTabClicks(root);
    initLayoutSwitcher(root);
    initLoadMore(root);
  });
  refreshAllSampleSectionsFromCart().catch(() => {});
}

bindSamplesSections();
document.addEventListener('DOMContentLoaded', bindSamplesSections);
document.addEventListener('shopify:section:load', bindSamplesSections);
